/** @jsx isolateComponent */
import { css, keyframes } from '@emotion/core';
import {
  Colors,
  Typography,
  VisuallyHidden,
  useTheme,
} from '@zapier/design-system';
import { isolateComponent } from '@zapier/style-encapsulation';

const giggle = keyframes`
  25% {
    transform: scale(0.95) rotate(-9deg);
  }
  33% {
    transform: scale(0.96) rotate(-8deg);
  }
  66% {
    transform: scale(1.03) rotate(12deg);
  }
  74% {
    transform: scale(1.04) rotate(13deg);
  }
`;

// So clunky to do this without using CSS vars.
export const CssVars = {
  animationName: '--UniversalFooter__Branding-animation',
};

const Styles = {
  root: (isRebrand: boolean) => css`
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 5px;
    cursor: default;
    user-select: none;
    color: ${isRebrand ? Colors.neutral700 : Colors.gray60};
    ${CssVars.animationName}: none;
    &:hover,
    &:focus {
      color: ${isRebrand ? Colors.zapier : Colors.brand};
      ${CssVars.animationName}: ${giggle};
    }
  `,
  cell: (isRebrand: boolean) => css`
    flex-grow: 1;
    flex-shrink: 0;
    text-align: center;

    ${isRebrand && `flex-direction: row;`}
  `,
  logo: css`
    color: inherit;
    display: block;
    margin: 0 auto;
    fill: currentColor;
    transition: all 0.2s ease-in-out;
    animation: var(${CssVars.animationName}) 0.5s linear infinite;
  `,
  zapierLogo: css`
    margin: 0 6px 0 0;
  `,
  tagline: (isRebrand: boolean) => css`
    ${Typography.smallPrint1};
    display: grid;
    grid-template-columns: max-content max-content;
    gap: 3px;
    // Optically align
    ${isRebrand ? 'margin-bottom: 3px;' : 'margin-top: 3px;'}
    // For browsers that don't yet support Flexbox gap
    ${isRebrand ? 'padding: 0 2px;' : 'padding: 0 5px;'}
    justify-content: center;
  `,
  taglineText: (isRebrand: boolean) => css`
    color: ${isRebrand ? Colors.neutral700 : Colors.gray60};
    ${isRebrand && Typography.smallPrint1Semibold}
  `,
  taglineEmoticon: css`
    color: inherit;
    transition: all 0.2s ease-in-out;
    animation: var(${CssVars.animationName}) 0.4s linear infinite reverse;
  `,
};

// TODO: Potentially pull this from `@zapier/design-system-patterns`.
const ZapierLogo = ({ isRebrand }: { isRebrand: boolean }) =>
  isRebrand ? (
    <svg
      css={Styles.zapierLogo}
      data-testid="ZapierLogo"
      height="27"
      viewBox="0 0 32 27"
      width="32"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.35411 17.6433L8.58744 9.51639H2.49081L4.13116 0H31.4976L24.4441 9.51639H29.0022L23.2041 17.6433H29.0918L27.4515 27H0.0849609L7.01901 17.6433H2.35411Z"
        fill="#FF4F00"
      />
    </svg>
  ) : (
    <svg
      css={Styles.logo}
      data-testid="ZapierLogo"
      viewBox="0 0 80 37"
      width="70"
      height="32"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M53.9335 4.18463H50.9949L53.0729 2.11208C52.9095 1.88325 52.7272 1.66852 52.5286 1.47051C52.3301 1.2725 52.1154 1.09121 51.8859 0.928209L49.808 3.00076V0.069825C49.5361 0.024372 49.2564 0.000339343 48.972 -0.000183105H48.9662C48.6813 0.000339343 48.4021 0.024372 48.1302 0.069825V3.00076L46.0523 0.928209C45.8228 1.09121 45.6076 1.2725 45.4095 1.47051L45.4085 1.47156C45.21 1.66904 45.0282 1.88325 44.8653 2.11208L46.9433 4.18463H44.0047C44.0047 4.18463 43.9345 4.73477 43.9345 5.01951V5.02316C43.9345 5.30737 43.9586 5.58688 44.0047 5.85856H46.9433L44.8653 7.93059C45.1922 8.38826 45.5934 8.78897 46.0523 9.11446L48.1302 7.0419V9.97284C48.4021 10.0183 48.6807 10.0423 48.9652 10.0429H48.9725C49.2569 10.0423 49.5366 10.0183 49.808 9.97284V7.0419L51.8865 9.11446C52.1154 8.95198 52.3301 8.77017 52.5286 8.57216H52.5292C52.7272 8.37415 52.9095 8.15942 53.0729 7.93059L50.9944 5.85856H53.9335C53.9791 5.58741 54.0032 5.30894 54.0032 5.02473V5.01794C54.0032 4.73373 53.9791 4.45578 53.9335 4.18463ZM58.8497 25.5304C58.0105 24.7441 57.571 23.5038 57.5302 21.8095H68.1148C68.1347 21.588 68.1551 21.3215 68.175 21.0081C68.1955 20.6956 68.206 20.3984 68.206 20.1157C68.206 19.087 68.064 18.1346 67.7811 17.2574C67.4978 16.3802 67.0834 15.6242 66.5381 14.9889C65.9918 14.3536 65.3145 13.8547 64.5058 13.4916C63.697 13.1285 62.7568 12.9467 61.6856 12.9467C60.4316 12.9467 59.3447 13.1635 58.4249 13.5971C57.5051 14.0313 56.7419 14.6159 56.1353 15.3515C55.5292 16.0882 55.0788 16.9549 54.786 17.9528C54.4926 18.9517 54.346 20.0253 54.346 21.1742C54.346 22.3445 54.4979 23.4181 54.8011 24.3961C55.1044 25.3742 55.5848 26.2216 56.2416 26.9373C56.8985 27.6531 57.7329 28.2074 58.7439 28.6008C59.7543 28.9942 60.9674 29.1907 62.3833 29.1907C63.3534 29.1907 64.2376 29.1196 65.0369 28.9791C65.8347 28.8375 66.568 28.6264 67.2353 28.3438C67.2149 27.9201 67.1547 27.4922 67.0536 27.058C66.952 26.6244 66.821 26.2566 66.6591 25.9541C65.3648 26.4582 64.0008 26.7106 62.565 26.7106C60.9271 26.7106 59.6888 26.3167 58.8497 25.5304ZM30.5092 13.3096C30.7109 13.2699 30.9183 13.2396 31.1305 13.2192C31.3431 13.1993 31.56 13.1889 31.7826 13.1889C31.9843 13.1889 32.1969 13.1993 32.4195 13.2192C32.6416 13.2396 32.8543 13.2699 33.0565 13.3096C33.0764 13.3508 33.1021 13.4762 33.1319 13.6883C33.1623 13.8999 33.1927 14.1267 33.2231 14.3686C33.2535 14.6105 33.2838 14.843 33.3142 15.0645C33.3446 15.2865 33.3593 15.4276 33.3593 15.4882C33.5615 15.1653 33.804 14.8529 34.0874 14.5504C34.3702 14.2479 34.7096 13.9752 35.1035 13.7338C35.4975 13.4914 35.9422 13.3002 36.4382 13.1591C36.9327 13.018 37.4837 12.947 38.0908 12.947C39.0002 12.947 39.8445 13.0985 40.6235 13.401C41.4013 13.7035 42.0681 14.1721 42.6244 14.8069C43.1802 15.4427 43.6149 16.2494 43.9287 17.2269C44.2419 18.206 44.3985 19.3595 44.3985 20.6907C44.3985 23.3526 43.6757 25.4351 42.2305 26.9371C40.7843 28.4397 38.7377 29.191 36.0888 29.191C35.6441 29.191 35.1895 29.1607 34.7243 29.1001C34.2586 29.0395 33.8548 28.9585 33.5112 28.8582V35.9666C33.2686 36.0063 33.0109 36.0366 32.738 36.057C32.4651 36.0768 32.2168 36.0873 31.9947 36.0873C31.7721 36.0873 31.5249 36.0768 31.252 36.057C30.9791 36.0366 30.7313 36.0063 30.5092 35.9666V13.3096ZM24.2918 18.6036C24.2918 17.4135 23.9885 16.5869 23.382 16.123C22.7754 15.6596 21.8959 15.4276 20.7435 15.4276C20.0353 15.4276 19.3738 15.4835 18.7572 15.5938C18.1402 15.705 17.5383 15.8508 16.9527 16.0321C16.5682 15.3665 16.3765 14.5708 16.3765 13.6424C17.0632 13.4209 17.8212 13.2495 18.6509 13.1283C19.4796 13.0071 20.2779 12.947 21.0468 12.947C23.0682 12.947 24.605 13.4063 25.6563 14.3232C26.7076 15.2411 27.2335 16.7082 27.2335 18.7243V28.4345C26.5259 28.596 25.6668 28.7621 24.6559 28.9335C23.6444 29.1043 22.613 29.1905 21.5622 29.1905C20.5712 29.1905 19.677 29.1001 18.8782 28.9183C18.0794 28.7365 17.4021 28.445 16.8464 28.0406C16.2901 27.6383 15.8606 27.1232 15.5573 26.4983C15.254 25.8735 15.1026 25.1175 15.1026 24.2293C15.1026 23.3631 15.2791 22.6014 15.6332 21.9457C15.9868 21.2911 16.4671 20.7462 17.0737 20.3125C17.6803 19.8794 18.378 19.556 19.1663 19.3449C19.9552 19.1328 20.7839 19.0268 21.6534 19.0268C22.3003 19.0268 22.8309 19.0425 23.2452 19.0722C23.6596 19.1031 24.0084 19.1381 24.2918 19.1783V18.6036ZM0.666687 28.4045L8.8554 15.6996H1.63731C1.57654 15.3365 1.54616 14.9337 1.54616 14.4896C1.54616 14.0664 1.57654 13.6725 1.63731 13.3094H13.3135L13.4649 13.7033L5.21597 26.438H12.9495C13.0097 26.8419 13.0406 27.2546 13.0406 27.6778C13.0406 28.0822 13.0097 28.4646 12.9495 28.8282H0.818592L0.666687 28.4045ZM23.1394 21.417C23.6046 21.4577 23.9885 21.4974 24.2918 21.5377V26.4685C23.9073 26.5694 23.4679 26.6446 22.9723 26.6948C22.4773 26.746 21.997 26.771 21.5319 26.771C21.1683 26.771 20.7839 26.7512 20.3795 26.7104C19.9751 26.6697 19.6063 26.5589 19.2727 26.3776C18.939 26.1958 18.6609 25.9341 18.4388 25.5908C18.2156 25.2486 18.1051 24.7742 18.1051 24.1692C18.1051 23.222 18.433 22.5162 19.0909 22.0518C19.7472 21.5883 20.7027 21.3559 21.9567 21.3559C22.2799 21.3559 22.6743 21.3768 23.1394 21.417ZM34.6939 26.5892C34.2895 26.5291 33.8946 26.4283 33.5112 26.2867V19.9953C33.5112 19.2284 33.6222 18.5691 33.8454 18.0137C34.0675 17.4594 34.3603 17.0001 34.7243 16.637C35.0884 16.2739 35.5022 16.0017 35.9678 15.821C36.4324 15.6392 36.918 15.5483 37.4235 15.5483C38.7775 15.5483 39.7534 16.0226 40.35 16.9698C40.9461 17.9181 41.2447 19.1986 41.2447 20.8119C41.2447 21.8203 41.1179 22.6922 40.8654 23.4278C40.6124 24.1645 40.2688 24.7742 39.8341 25.258C39.3998 25.7423 38.8792 26.1012 38.2726 26.3321C37.666 26.5641 36.9992 26.6801 36.2711 26.6801C35.6237 26.6801 35.0983 26.6493 34.6939 26.5892ZM48.129 15.6693H46.1574C46.1171 15.5079 46.0867 15.3214 46.0668 15.1098C46.0463 14.8982 46.0364 14.6913 46.0364 14.4896C46.0364 14.2885 46.0463 14.0811 46.0668 13.8695C46.0867 13.6579 46.1171 13.4714 46.1574 13.3094H51.0713V28.8282C50.8481 28.8679 50.6009 28.8977 50.328 28.9181C50.0551 28.9379 49.8068 28.9484 49.5847 28.9484C49.3825 28.9484 49.1447 28.9379 48.8723 28.9181C48.5989 28.8977 48.3511 28.8679 48.129 28.8282V15.6693ZM65.2339 19.5718C65.2339 19.0071 65.1579 18.4679 65.0065 17.9528C64.8546 17.4387 64.632 16.9904 64.3392 16.607C64.0453 16.224 63.6719 15.9163 63.2167 15.6848C62.762 15.4529 62.2209 15.3364 61.5944 15.3364C60.3609 15.3364 59.4159 15.7099 58.7585 16.456C58.1017 17.202 57.7025 18.2407 57.5606 19.5718H65.2339ZM72.1499 13.2192C71.9477 13.2396 71.7351 13.2699 71.513 13.3096V28.8279C71.7555 28.8676 72.0132 28.8979 72.2866 28.9183C72.5595 28.9382 72.8068 28.9486 73.0299 28.9486C73.2515 28.9486 73.4998 28.9382 73.7722 28.9183C74.0451 28.8979 74.2928 28.8676 74.5154 28.8279V21.114C74.5154 20.0659 74.6317 19.2086 74.8643 18.5435C75.0964 17.8779 75.4101 17.3534 75.804 16.9699C76.1984 16.5869 76.6426 16.32 77.1387 16.1685C77.6342 16.0175 78.1549 15.9417 78.7007 15.9417H79.1098C79.2816 15.9417 79.4482 15.9621 79.6105 16.0018C79.6509 15.7604 79.6865 15.5081 79.7169 15.2458C79.7467 14.9841 79.7619 14.7317 79.7619 14.4898C79.7619 14.2678 79.752 14.0562 79.7315 13.8545C79.7116 13.6534 79.6807 13.4611 79.6409 13.2793C79.5199 13.26 79.3722 13.2448 79.2009 13.2344C79.0291 13.2239 78.862 13.2192 78.7007 13.2192C77.6085 13.2192 76.7144 13.4716 76.0167 13.9752C75.3195 14.4794 74.758 15.075 74.3337 15.7604C74.3337 15.4172 74.3028 14.9992 74.2425 14.5045C74.1818 14.0113 74.121 13.6121 74.0608 13.3096C73.8785 13.2699 73.6763 13.2396 73.4542 13.2192C73.2311 13.1994 73.009 13.1889 72.7869 13.1889C72.5643 13.1889 72.3516 13.1994 72.1499 13.2192ZM50.0343 6.08373C50.1589 5.75355 50.2276 5.39671 50.2276 5.02368V5.01898C50.2276 4.64595 50.1589 4.2886 50.0343 3.95893C49.7037 3.83459 49.3455 3.76615 48.9715 3.76615H48.9667C48.5927 3.76615 48.2345 3.83459 47.9034 3.95893C47.7793 4.2886 47.7106 4.64595 47.7101 5.01898V5.02368C47.7106 5.39671 47.7793 5.75407 47.9039 6.08373C48.2345 6.20808 48.5927 6.27652 48.9667 6.27652H48.9715C49.3455 6.27652 49.7037 6.20808 50.0343 6.08373Z"
      />
    </svg>
  );

const ZapierName = () => (
  <svg
    css={Styles.zapierLogo}
    fill="none"
    height="25"
    viewBox="0 0 71 25"
    width="71"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M35.2592 5.70142C33.2924 5.70142 31.662 6.71585 30.9374 7.88636V6.01355H27.5215V24.4836H30.9374V17.4585C31.662 18.629 33.2924 19.6434 35.2592 19.6434C38.908 19.6434 41.7029 16.6261 41.7029 12.6724C41.7029 8.74473 38.908 5.70142 35.2592 5.70142ZM34.5863 8.6667C36.6825 8.6667 38.3387 10.3834 38.3387 12.6724C38.3387 14.9614 36.6825 16.7042 34.5863 16.7042C32.4643 16.7042 30.8339 14.9614 30.8339 12.6724C30.8339 10.3834 32.4643 8.6667 34.5863 8.6667Z"
      fill="#FF4F00"
    />
    <path
      d="M48.2656 12.6936C48.2656 16.6993 50.7679 19.6385 55.6151 19.6385C57.2713 19.6385 59.2963 19.1068 60.6161 18.1964V15.1068C59.4774 16.0432 57.5819 16.7773 56.0292 16.7773C53.1401 16.7773 51.4746 15.523 51.4746 13.5519H61.3601C61.386 13.2658 61.386 13.0057 61.386 12.7456C61.386 8.79187 59.264 5.69653 55.0717 5.69653C51.4487 5.69653 48.2656 8.68782 48.2656 12.6936ZM55.0199 8.45372C56.9608 8.45372 58.0218 9.85833 58.1771 11.2629H51.5781C52.0439 9.54619 53.3637 8.45372 55.0199 8.45372Z"
      fill="#FF4F00"
    />
    <path
      d="M70.5336 5.8003C70.4042 5.8003 70.2748 5.77429 70.1454 5.77429C68.3339 5.77429 66.9623 6.78873 66.2377 8.06328V6.00839H62.8218V19.3261H66.2377V12.8233C66.2377 10.2482 67.7646 9.18176 69.7313 9.18176C69.9901 9.18176 70.2489 9.20777 70.5336 9.23378V5.8003Z"
      fill="#FF4F00"
    />
    <path
      d="M43.2534 5.96692V19.2847H46.6694V5.96692H43.2534Z"
      fill="#FF4F00"
    />
    <path
      d="M22.2318 19.2794H25.5443V10.8518C25.5443 7.60038 23.3705 5.64954 19.8251 5.64954C18.0912 5.64954 16.4091 6.11774 15.374 6.68999V9.70729C16.435 9.08302 17.7031 8.61482 19.1005 8.61482C21.0932 8.61482 22.2318 9.65526 22.2318 11.0078V11.9963C21.6884 11.424 20.4462 10.8258 18.9452 10.8258C16.1762 10.8258 13.873 12.5685 13.873 15.1436C13.873 17.8748 16.1762 19.5915 18.8417 19.5915C20.4979 19.5915 21.7401 18.8892 22.2318 18.265V19.2794ZM22.2836 15.1436C22.2836 16.3662 20.9379 17.0424 19.6181 17.0424C18.2724 17.0424 17.0043 16.3662 17.0043 15.1436C17.0043 13.9211 18.2724 13.2188 19.6181 13.2188C20.9379 13.2188 22.2836 13.9211 22.2836 15.1436Z"
      fill="#FF4F00"
    />
    <path
      d="M5.8033 16.5073L13.3828 5.92078H1.14806V8.65195H7.94548L0.222168 19.2385H12.8077V16.5073H5.8033Z"
      fill="#FF4F00"
    />
    <path
      d="M44.825 0H44.8509C45.9647 0 46.8454 0.876102 46.8454 2.01769C46.8454 3.15928 45.9647 4.03538 44.8509 4.03538H44.825C43.7112 4.03538 42.8306 3.15928 42.8306 2.01769C42.8306 0.876102 43.7112 0 44.825 0Z"
      fill="#FF4F00"
    />
  </svg>
);

/**
 * @private
 * Renders the logo and tagline.
 */
export const Branding = () => {
  const { isRebrand } = useTheme();

  return (
    <div css={Styles.root(isRebrand)}>
      <VisuallyHidden>Zapier makes you happier :)</VisuallyHidden>
      <div aria-hidden={true} css={Styles.cell(isRebrand)}>
        <ZapierLogo isRebrand={isRebrand} />
        {isRebrand && <ZapierName />}
      </div>
      <div aria-hidden={true} css={Styles.cell}>
        <p css={Styles.tagline(isRebrand)} data-testid="tagline">
          <span css={Styles.taglineText(isRebrand)}>
            makes you happier{isRebrand && ' :)'}
          </span>
          {!isRebrand && <span css={Styles.taglineEmoticon}>:)</span>}
        </p>
      </div>
    </div>
  );
};
